import { useEffect, useState } from 'react';
import { companiesApi } from '../api';
import { useAuth } from '../hooks/useAuth';
import SearchableSelect from './SearchableSelect';

interface Props {
  company?: any;
  onClose: () => void;
  onSave: () => void;
}

export default function CompanyModal({ company, onClose, onSave }: Props) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: company?.name || '',
    inn: company?.inn || '',
    type: company?.type || 'CUSTOMER',
    industry: company?.industry || '',
    website: company?.website || '',
    phones: company?.phones?.join(', ') || '',
    emails: company?.emails?.join(', ') || '',
    address: company?.address || '',
    description: company?.description || '',
    tags: company?.tags?.join(', ') || '',
    responsibleId: company?.responsibleId || '',
  });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);

  useEffect(() => {
    if (!company && user?.id && !form.responsibleId) {
      setForm(prev => ({ ...prev, responsibleId: user.id }));
    }
  }, [user]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await fetch('/api/users', { headers: { Authorization: `Bearer ${localStorage.getItem('svcrm_token')}` } });
        const data = await res.json();
        setUsers(data.data || []);
      } catch {}
    };
    loadUsers();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...form,
        inn: form.inn || null,
        phones: form.phones.split(',').map((s: string) => s.trim()).filter(Boolean),
        emails: form.emails.split(',').map((s: string) => s.trim()).filter(Boolean),
        tags: form.tags.split(',').map((s: string) => s.trim()).filter(Boolean),
        responsibleId: form.responsibleId || null,
      };
      if (company) {
        await companiesApi.update(company.id, data);
      } else {
        await companiesApi.create(data);
      }
      onSave();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>{company ? 'Редактировать' : 'Новая компания'}</h3>
          <button onClick={onClose} style={{ background: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Название *</label>
              <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="form-group">
              <label className="form-label">ИНН</label>
              <input className="form-input" value={form.inn} onChange={e => setForm({ ...form, inn: e.target.value })} placeholder="10 или 12 цифр" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Тип</label>
                <select className="form-select" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                  <option value="CUSTOMER">Клиент</option>
                  <option value="PARTNER">Партнер</option>
                  <option value="VENDOR">Поставщик</option>
                  <option value="OTHER">Другое</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Отрасль</label>
                <input className="form-input" value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Сайт</label>
              <input className="form-input" value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Телефоны (через запятую)</label>
                <input className="form-input" value={form.phones} onChange={e => setForm({ ...form, phones: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Email (через запятую)</label>
                <input className="form-input" value={form.emails} onChange={e => setForm({ ...form, emails: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Адрес</label>
              <input className="form-input" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Описание</label>
              <textarea className="form-input" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Теги (через запятую)</label>
              <input className="form-input" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} />
            </div>
            <div className="form-group">
              <SearchableSelect
                label="Ответственный"
                value={form.responsibleId}
                onChange={val => setForm({ ...form, responsibleId: val })}
                options={users.map((u: any) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))}
                emptyLabel="— Не назначен —"
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Сохранение...' : 'Сохранить'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
